import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import type { BlobId, PhaseId, PhotoId, ProjectId, WeeklyReviewId } from '~/types/ids'
import type { RecordEnvelope, ValidationIssues } from './common'
import { issue } from './common'

/**
 * A progress photo (D-081).
 *
 * The record holds metadata only; the bytes live in `blobs`, which is what lets
 * the Photos grid list a year of photos without decoding any of them.
 *
 * **Two blobs per photo, and the asymmetry between them matters.** `blobId` is
 * the stored image and is authoritative. `thumbnailBlobId` is derived, exists
 * to keep the grid cheap, and is never the source of a comparison — losing one
 * is a cosmetic defect, losing the other is data loss.
 *
 * **Nothing here records where the photo was taken, what took it, or when the
 * camera thought it was.** The capture pipeline re-encodes through a canvas, so
 * EXIF, GPS and device metadata are gone before a record is ever built (D-081).
 * `capturedDate` is the app's own local calendar date (D-034), not the camera's.
 */

export const PHOTO_VIEWS = ['front', 'side', 'back'] as const
export type PhotoView = (typeof PHOTO_VIEWS)[number]

export const PHOTO_VIEW_LABELS: Readonly<Record<PhotoView, string>> = {
  front: 'Front',
  side: 'Side',
  back: 'Back',
}

export interface PhotoRecord extends RecordEnvelope {
  readonly id: PhotoId
  readonly view: PhotoView
  /** The local date the app captured it on (D-034), never an EXIF timestamp. */
  readonly capturedDate: CalendarDate
  readonly capturedAt: IsoTimestamp
  /** The stored image. Authoritative. */
  readonly blobId: BlobId
  /** The derived grid thumbnail. Regenerable; never authoritative. */
  readonly thumbnailBlobId: BlobId
  readonly widthPx: number
  readonly heightPx: number
  readonly byteSize: number
  /**
   * How the image reached the app.
   *
   * Recorded because the two paths differ in what the user could control: a
   * guided capture had the alignment overlay, a picked file did not, so a
   * comparison across the two is less like-for-like than a reader would assume.
   */
  readonly source: 'guidedCapture' | 'pickedFile'
  readonly reviewId?: WeeklyReviewId
  readonly phaseId?: PhaseId
  readonly projectId?: ProjectId
  readonly note?: string
}

export function isPhotoView(value: unknown): value is PhotoView {
  return typeof value === 'string' && (PHOTO_VIEWS as readonly string[]).includes(value)
}

/**
 * Validate a record before it is written.
 *
 * Dimensions are checked because a zero on either axis means the canvas step
 * produced nothing, and a record pointing at an empty image is worse than a
 * failed capture: it looks like a photo in the grid and shows a blank on tap.
 */
export function validatePhoto(record: PhotoRecord): ValidationIssues {
  const issues: ReturnType<typeof issue>[] = []

  if (!isPhotoView(record.view)) {
    issues.push(issue('view', 'A photo must be a front, side or back view.'))
  }
  if (!(record.widthPx > 0) || !(record.heightPx > 0)) {
    issues.push(issue('widthPx', 'A photo must have a real width and height.'))
  }
  if (!(record.byteSize > 0)) {
    issues.push(issue('byteSize', 'A photo must have image data.'))
  }
  if (record.blobId === record.thumbnailBlobId) {
    /* One id for both would mean deleting the photo removes the image the
       thumbnail still points at, or vice versa. They are separate blobs. */
    issues.push(issue('thumbnailBlobId', 'The image and its thumbnail must be separate blobs.'))
  }

  return issues
}

/**
 * Photos of one view, newest first.
 *
 * The comparison screens read a view's timeline, and "newest first" is the
 * order every list in this app uses for history.
 */
export function photosForView(
  photos: readonly PhotoRecord[],
  view: PhotoView,
): readonly PhotoRecord[] {
  return photos
    .filter((photo) => photo.view === view)
    .slice()
    .sort((left, right) => right.capturedAt.localeCompare(left.capturedAt))
}

/**
 * The earliest and latest photo of one view.
 *
 * **Returns nothing when there is only one photo**, because a comparison needs
 * two genuinely different moments. Returning the same photo twice would render
 * as a side-by-side showing no change — a missing value drawn as a zero, which
 * this project forbids at every other scale too.
 */
export function comparisonPair(
  photos: readonly PhotoRecord[],
  view: PhotoView,
): { readonly earliest: PhotoRecord; readonly latest: PhotoRecord } | undefined {
  const ordered = photosForView(photos, view)
  if (ordered.length < 2) return undefined

  const latest = ordered[0]
  const earliest = ordered[ordered.length - 1]
  if (latest === undefined || earliest === undefined) return undefined

  return { earliest, latest }
}

/**
 * The two most recent photos of one view (Checkpoint 5.2).
 *
 * `ui-ux-spec.md` asks for previous-versus-current alongside
 * start-versus-current, and they answer different questions: the first shows
 * what a fortnight did, the second what the whole project did.
 *
 * **Returns nothing with fewer than two photos**, for the same reason
 * `comparisonPair` does — showing one photo twice would render as a comparison
 * with no change, which is a missing value drawn as a zero.
 */
export function previousPair(
  photos: readonly PhotoRecord[],
  view: PhotoView,
): { readonly earliest: PhotoRecord; readonly latest: PhotoRecord } | undefined {
  const ordered = photosForView(photos, view)
  if (ordered.length < 2) return undefined

  const latest = ordered[0]
  const previous = ordered[1]
  if (latest === undefined || previous === undefined) return undefined

  return { earliest: previous, latest }
}

export interface PhotoMonth {
  /** `YYYY-MM`, taken from the stored calendar date rather than a timestamp. */
  readonly month: string
  readonly photos: readonly PhotoRecord[]
}

/**
 * Photos grouped by calendar month, newest month first.
 *
 * The contact sheet `ui-ux-spec.md` asks for. Grouping uses `capturedDate` —
 * the app's own local date (D-034) — never `capturedAt`, because a photo taken
 * at 00:30 belongs to the day the user thinks they took it, and near a month
 * boundary a UTC timestamp puts it in the wrong month entirely.
 */
export function groupByMonth(photos: readonly PhotoRecord[]): PhotoMonth[] {
  const months = new Map<string, PhotoRecord[]>()

  for (const photo of photos) {
    const month = (photo.capturedDate as string).slice(0, 7)
    const existing = months.get(month)
    if (existing === undefined) months.set(month, [photo])
    else existing.push(photo)
  }

  return [...months.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([month, group]) => ({
      month,
      photos: group.slice().sort((left, right) => right.capturedAt.localeCompare(left.capturedAt)),
    }))
}
